import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { type Team, getTeamStickerCodes } from '../../data/stickers';
import { StickerCard } from './StickerCard';
import { FlagRound } from './FlagRound';
import { TeamProgressBar } from './TeamProgressBar';

export type Filter = 'all' | 'missing' | 'duplicates';

interface WishlistShape {
  sticker_code: string;
  priority: number;
}

interface Props {
  team: Team;
  counts: Record<string, number>;
  onStickerClick: (code: string) => void;
  onStickerReset: (code: string) => void;
  searchQuery: string;
  filter: Filter;
  defaultOpen?: boolean;
  wishlistByCode?: Record<string, WishlistShape>;
  onTogglePriority?: (code: string) => void;
}

export function TeamSection({
  team,
  counts,
  onStickerClick,
  onStickerReset,
  searchQuery,
  filter,
  defaultOpen = true,
  wishlistByCode,
  onTogglePriority,
}: Props) {
  const [open, setOpen] = useState(defaultOpen);
  const allCodes = getTeamStickerCodes(team.code);

  const visibleCodes = allCodes.filter((c) => {
    if (filter === 'missing') return (counts[c] || 0) === 0;
    if (filter === 'duplicates') return (counts[c] || 0) >= 2;
    return true;
  });

  if (searchQuery) {
    const q = searchQuery.toUpperCase();
    const teamMatches = team.name.toUpperCase().includes(q) || team.code.includes(q);
    const hasMatch = visibleCodes.some((c) => c.includes(q));
    if (!teamMatches && !hasMatch) return null;
  }

  if (filter !== 'all' && visibleCodes.length === 0) return null;

  const owned = allCodes.filter((c) => (counts[c] || 0) >= 1).length;
  const total = allCodes.length;
  const percent = total > 0 ? (owned / total) * 100 : 0;
  const complete = owned === total;

  return (
    <section className="bg-white rounded-xl shadow-sm p-5 mb-4 border border-ink-300/60">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex items-center gap-3 w-full bg-transparent border-none p-0 text-left btn-focus rounded-md"
      >
        <FlagRound code={team.code} size={36} />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-3 mb-1.5">
            <h2 className="font-display font-semibold text-[20px] leading-[26px] text-ink-900 tracking-[-0.01em] truncate">
              {team.name}
              {team.group.length === 1 && (
                <span className="ml-1.5 text-[15px] font-medium text-ink-500 tabular-nums">
                  ({team.code})
                </span>
              )}
            </h2>
            <span
              className={[
                'font-display font-bold text-small tabular-nums whitespace-nowrap',
                complete ? 'text-green-700' : 'text-ink-500',
              ].join(' ')}
            >
              {owned}/{total}
            </span>
          </div>
          <TeamProgressBar teamCode={team.code} percent={percent} />
        </div>
        <ChevronDown
          size={20}
          className={[
            'text-ink-500 shrink-0 transition-transform duration-base ease-out',
            open ? 'rotate-180' : 'rotate-0',
          ].join(' ')}
        />
      </button>

      {open && (
        <div
          className="grid gap-2 mt-4"
          style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(56px, 1fr))' }}
        >
          {visibleCodes.map((code) => {
            const w = wishlistByCode?.[code];
            return (
              <StickerCard
                key={code}
                code={code}
                count={counts[code] || 0}
                size="sm"
                showCode
                onClick={() => onStickerClick(code)}
                onLongPress={() => onStickerReset(code)}
                isPriority={!!w}
                priorityRank={w?.priority}
                onTogglePriority={
                  onTogglePriority ? () => onTogglePriority(code) : undefined
                }
              />
            );
          })}
        </div>
      )}
    </section>
  );
}
